import React, { useContext, useEffect, useState } from "react";
import { Button, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiCheckCircle, BiEdit } from "react-icons/bi";
import { MdAddCircleOutline } from "react-icons/md";
import { toast } from "react-toastify";
import { LoaderContext, RegulationContext } from "../../../App";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import { useCallbackPrompt } from "../../../core/hooks/useCallbackPrompt";
import CarbonEmissionService from "../../../core/service/CarbonEmissionService";
import './CarbonEmission.scss';

const CarbonEmissionCalculator = ({ t, filter, viewToggled, confirmViewChange }) => {
	const [activeScope, setActiveScope] = useState('Scope 1');
	const [emissionTable, setEmissionTable] = useState('DEFRA');
	const [categories, setCategories] = useState([]);
	const [materials, setMaterials] = useState([{
		level1: '',
		level2: '',
		level3: '',
		level4: '',
		level5: '',
		quantity: '',
		multiple: '',
		unit: '',
		emission: ''
	}]);
	const [materialsOptions, setMaterialsOptions] = useState({
		level1: [],
		level2: [],
		level3: [],
		level4: [],
		level5: [],
		uoms: []
	});
	const [selectedCat, setSelectedCat] = useState(0);
	const [liveGHGEmission, setLiveGHGEmission] = useState({});
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [disabledMatRows, setDisabledMatRows] = useState({});
	const [dirty, setDirty] = useState(false);
	const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(dirty);
	const [showWarning, setShowWarning] = useState(false);
	const [pausedScopeToNavigate, setPausedScopeToNavigate] = useState(null);
	const [pausedCatToNavigate, setPausedCatToNavigate] = useState(0);
	const [validation, setValidation] = useState({});
	const [aggEmission, setAggEmission] = useState(0);
	const [aggCatEmission, setAggCatEmission] = useState(0);
	const { glbRegulation } = useContext(RegulationContext);
	const [dataToDelete, setDataToDelete] = useState({});
	const [dbData, setDBData] = useState([]);

	useEffect(() => {
		if (!viewToggled.on) return;

		if (viewToggled.to === 'TABLE' && dirty) {
			setShowWarning(true);
			confirmViewChange(false);
		} else {
			confirmViewChange(true);
		}
	}, [viewToggled, dirty]);


	useEffect(() => {
		getCats(activeScope);
		if (!filter.entity) return;

		getAggregatedEmission(activeScope);
	}, [activeScope]);

	useEffect(() => {
		if (selectedCat === 0) return;

		getEmissions(filter, selectedCat, emissionTable);
	}, [filter, selectedCat, emissionTable]);

	useEffect(() => {
		if (!filter.entity) return;

		getAggregatedEmission(activeScope);
	}, [filter]);

	useEffect(() => {
		getDBData();
	}, []);

	const getCats = (scope) => {
		CarbonEmissionService.getCategories(scope).then(res => {
			if (res.status !== 200) return;

			setCategories(res.data.data);
			setLiveGHGEmission({});
			setValidation({});
		}).catch(err => { });
	};

	const onCatSelect = (cat, row = null) => {
		if (dirty) {
			setPausedCatToNavigate(cat.id);
			setPausedScopeToNavigate(null);
			setShowWarning(true);
			return;
		} else {
			setPausedCatToNavigate(0);
			setShowWarning(false);
		}
		setSelectedCat(cat.id);
	};

	const getLevels = (row, cat) => {
		CarbonEmissionService.getLevels({
			db: emissionTable,
			scope: activeScope,
			category: cat.category,
			level: row.level,
			level1: row.level1,
			level2: row.level2,
			level3: row.level3,
			level4: row.level4,
			level5: row.level5,
		}).then(res => {
			if (res.status !== 200) return;

			const options = res.data.data;
			setMaterialsOptions(moptions => {
				const matOptions = JSON.parse(JSON.stringify(moptions));
				if (row.level === 1) {
					matOptions.level1[row.index] = options;
				} else if (row.level === 2) {
					matOptions.level2[row.index] = options;
				} else if (row.level === 3) {
					matOptions.level3[row.index] = options;
				} else if (row.level === 4) {
					matOptions.level4[row.index] = options;
				} else if (row.level === 5) {
					matOptions.level5[row.index] = options;
				}
				return matOptions;
			});
		}).catch(err => { });
	};

	const onScopeChange = (scope) => {
		if (dirty) {
			setPausedScopeToNavigate(scope);
			setPausedCatToNavigate(0);
			setShowWarning(true);
			return;
		} else {
			setPausedScopeToNavigate(null);
			setShowWarning(false);
		}
		setMaterialsOptions({
			level1: [],
			level2: [],
			level3: [],
			level4: [],
			level5: [],
		});
		setSelectedCat(0);
		setActiveScope(scope);
	};

	const onLevel1Change = (val, index) => {
		const row = {
			index: index,
			level: 2,
			level1: val,
			level2: '',
			level3: '',
			level4: '',
			level5: ''
		};
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].level1 = val;
		matData[index].level2 = '';
		matData[index].level3 = '';
		matData[index].level4 = '';
		matData[index].level5 = '';
		matData[index].quantity = '';
		matData[index].multiple = '';
		matData[index].unit = '';
		matData[index].emission = '';
		setMaterials(matData);
		const cat = categories?.find(cat => cat.id === selectedCat);
		getLevels(row, cat);
		setDirty(true);
	};

	const onLevel2Change = (val, index) => {
		const row = {
			index: index,
			level: 3,
			level1: materials[index].level1,
			level2: val,
			level3: '',
			level4: '',
			level5: '',
		};
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].level2 = val;
		matData[index].level3 = '';
		matData[index].level4 = '';
		matData[index].level5 = '';
		matData[index].quantity = '';
		matData[index].multiple = '';
		matData[index].unit = '';
		matData[index].emission = '';
		setMaterials(matData);
		const cat = categories?.find(cat => cat.id === selectedCat);
		getLevels(row, cat);
		setDirty(true);
	};

	const onLevel3Change = (val, index) => {
		const row = {
			index: index,
			level: 4,
			level1: materials[index].level1,
			level2: materials[index].level2,
			level3: val,
			level4: '',
			level5: '',
		};
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].level3 = val;
		matData[index].level4 = '';
		matData[index].level5 = '';
		matData[index].quantity = '';
		matData[index].multiple = '';
		matData[index].unit = '';
		matData[index].emission = '';
		setMaterials(matData);
		const cat = categories?.find(cat => cat.id === selectedCat);
		getLevels(row, cat);
		getUOMs({ level3: val, index: index });
		setDirty(true);
	};

	const onLevel4Change = (val, index) => {
		const row = {
			index: index,
			level: 5,
			level1: materials[index].level1,
			level2: materials[index].level2,
			level3: materials[index].level3,
			level4: val,
			level5: '',
		};
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].level4 = val;
		matData[index].level5 = '';
		matData[index].quantity = '';
		matData[index].multiple = '';
		matData[index].unit = '';
		matData[index].emission = '';
		setMaterials(matData);
		const cat = categories?.find(cat => cat.id === selectedCat);
		getLevels(row, cat);
		getUOMs({ level4: val, index: index });
		setDirty(true);
	};

	const onLevel5Change = (val, index) => {
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].level5 = val;
		matData[index].quantity = '';
		matData[index].multiple = '';
		matData[index].unit = '';
		matData[index].emission = '';
		setMaterials(matData);
		getUOMs({ level5: val, index: index });
		setDirty(true);
	};

	const getUOMs = ({ level3, level4, level5, index }) => {
		CarbonEmissionService.getUOM({
			db: emissionTable,
			scope: activeScope,
			level1: materials[index].level1,
			level2: materials[index].level2,
			level3: level3 ?? materials[index].level3,
			level4: level4 ?? materials[index].level4,
			level5: level5 ?? materials[index].level5
		}).then(res => {
			if (res.status !== 200) return;

			const uoms = res.data.data;
			const matOptions = JSON.parse(JSON.stringify(materialsOptions));
			matOptions.uoms[index] = uoms;
			setMaterialsOptions(matOptions);
		}).catch(err => { });
	};

	const onUnitChange = (val, index) => {
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].unit = val;
		setMaterials(matData);
		getFields(val, index);
		getLiveData(index, null, val);
		setDirty(true);
	};

	const onQuantityChange = (val, index) => {
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].quantity = val;
		setMaterials(matData);
		setDirty(true);
	};

	const onMultipleChange = (val, index) => {
		const matData = JSON.parse(JSON.stringify(materials));
		matData[index].multiple = val;
		setMaterials(matData);
		setDirty(true);
	};

	const onQuantityBlur = (val, index) => {
		getLiveData(index, val, null);
	};

	const onMultipleBlur = (val, index) => {
		getLiveData(index, null, null, val);
	};

	const onDeleteRow = (index, id) => {
		if (!id) {
			deleteAtIndex(index);
			return;
		}
		setDataToDelete({ id: id, index: index });
	};

	const deleteAtIndex = (index) => {
		const matData = JSON.parse(JSON.stringify(materials));
		matData.splice(index, 1);
		setMaterials(matData);
		const matOptions = JSON.parse(JSON.stringify(materialsOptions));
		matOptions.level1.splice(index, 1);
		matOptions.level2.splice(index, 1);
		matOptions.level3.splice(index, 1);
		matOptions.level4.splice(index, 1);
		matOptions.level5.splice(index, 1);
		matOptions.uoms.splice(index, 1);
		setMaterialsOptions(matOptions);
		const liveData = JSON.parse(JSON.stringify(liveGHGEmission));
		Object.keys(liveData)?.forEach(d => {
			const indexKeys = Object.keys(liveData[d]);
			indexKeys?.forEach(iKey => {
				if (+iKey !== index) return;

				delete liveData[d][iKey];
			});
		});
		setLiveGHGEmission(liveData);
	};

	const onAddRow = (index) => {
		if (!materials[index].level1 || !materials[index].level2 || !materials[index].level3
			|| materials[index].quantity === '' || materials[index].emission === '') {
			validateRows(index);
			return;
		}
		const matData = JSON.parse(JSON.stringify(materials));
		matData.push({
			level1: '',
			level2: '',
			level3: '',
			level4: '',
			level5: '',
			quantity: '',
			mulitple: '',
			unit: '',
			emission: ''
		});
		setMaterials(matData);
		const matOptions = JSON.parse(JSON.stringify(materialsOptions));
		matOptions.level1.push([]);
		matOptions.level2.push([]);
		matOptions.level3.push([]);
		matOptions.level4.push([]);
		matOptions.level5.push([]);
		matOptions.uoms.push([]);
		setMaterialsOptions(matOptions);

		const row = {
			index: index + 1,
			level: 1,
			level1: '',
			level2: '',
			level3: '',
			level4: '',
			level5: '',
		};
		const cat = categories?.find(cat => cat.id === selectedCat);
		getLevels(row, cat);
		setDirty(true);
	};

	const onEditRow = (index) => {
		const rows = JSON.parse(JSON.stringify(disabledMatRows));
		if (rows[index]) {
			rows[index] = false;
		}
		setDisabledMatRows(rows);
		populateAllLevelAndUnitOptionsForRow(index);
	};

	const onSaveRow = (index) => {
		getLiveData(index);
		const rows = JSON.parse(JSON.stringify(disabledMatRows));
		delete rows[index];
		setDisabledMatRows(rows);
	};

	const getLiveData = (index, quantityVal = null, unitVal = null, multipleVal = null) => {
		const material = materials[index];
		const payload = {
			branchId: +filter.entity,
			scope: activeScope,
			category: categories?.find(cat => cat.id === selectedCat)?.category,
			level1: material.level1,
			level2: material.level2,
			level3: material.level3,
			level4: material.level4 ? material.level4 : null,
			level5: material.level5 ? material.level5 : null,
			uomBeforeConversion: unitVal ? unitVal : material.unit,
			db: emissionTable,
			valueBeforeConversion: quantityVal ? +quantityVal : +material.quantity,
			multiple: multipleVal ? +multipleVal : +material.multiple,
			period: filter.reportingPeriod
		};
		CarbonEmissionService.getLiveData(payload).then(res => {
			if (res.status !== 200) return;

			const data = res.data.data;
			const liveData = JSON.parse(JSON.stringify(liveGHGEmission));
			let emission = '';
			data?.forEach(d => {
				if (!liveData[`${d.ghgUnit}`]) {
					liveData[`${d.ghgUnit}`] = {};
				}
				liveData[`${d.ghgUnit}`][index] = [d.solution];
				if (d.ghgUnit === 'kg CO2e') {
					emission = d.solution;
				}
			});
			setLiveGHGEmission(liveData);
			setMaterials(mData => {
				const matData = JSON.parse(JSON.stringify(mData));
				matData[index].emission = emission;
				return matData;
			});
		}).catch(err => { });
	};

	const getGHGAggregate = (key) => {
		let total = 0;
		const indexKeys = liveGHGEmission[key] ? Object.keys(liveGHGEmission[key]) : [];
		indexKeys?.forEach(iKey => {
			liveGHGEmission[key][iKey]?.forEach(d => total += d);
		});
		return total;
	};

	const onSubmit = () => {
		const emissionData = [];
		let valid = true;
		for (let i = 0; i < materials.length; i++) {
			const material = materials[i];
			if (!material.level1 || !material.level2 || !material.level3
				|| material.quantity === '' || material.emission === '') {
				validateRows(i);
				valid = false;
				break;
			}

			emissionData.push({
				branchId: +filter.entity,
				scope: activeScope,
				category: categories?.find(cat => cat.id === selectedCat)?.category,
				level1: material.level1,
				level2: material.level2,
				level3: material.level3,
				level4: material.level4 ? material.level4 : null,
				level5: material.level5 ? material.level5 : null,
				uomBeforeConversion: material.unit,
				db: emissionTable,
				valueBeforeConversion: +material.quantity,
				multiple: +material.multiple,
				period: filter.reportingPeriod
			});
		}
		if (!valid) return;

		setLoaderDisplay(true);
		CarbonEmissionService.captureEmission({ emissionData: emissionData }, glbRegulation?.id).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			toast.success("Success", {
				position: toast.POSITION.TOP_RIGHT,
			});
			setDirty(false);
			setShowWarning(false);
			getEmissions(filter, selectedCat, emissionTable);
			getAggregatedEmission(activeScope);
		}).catch(err => {
			setLoaderDisplay(false);
			setDirty(false);
			setShowWarning(false);
		});
	};

	const getEmissions = (f, cat, db) => {
		setLoaderDisplay(true);
		const category = categories?.find(c => c.id === cat)?.category;
		CarbonEmissionService.getEmissions({
			scope: activeScope,
			category: category,
			db: db,
			period: f.reportingPeriod,
			businessUnitId: f.entity
		}).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			const emissions = res.data.data;
			if (!emissions.emissionData) {
				// If there is no data render as a new form
				resetMaterialForm();
				const r = {
					index: 0,
					level: 1,
					level1: '',
					level2: '',
					level3: '',
					level4: '',
					level5: '',
				};
				getLevels(r, categories?.find(c => c.id === cat));
				setAggCatEmission(0);
				return;
			}

			const matData = [], rows = {}, liveData = {};
			let catEmission = 0;
			const matOptions = {
				level1: [],
				level2: [],
				level3: [],
				level4: [],
				level5: [],
				uoms: []
			};
			emissions?.emissionData?.forEach((edata, index) => {
				matData.push({
					level1: edata.level1,
					level2: edata.level2,
					level3: edata.level3,
					level4: edata.level4,
					level5: edata.level5,
					quantity: edata.valueBeforeConversion,
					multiple: edata.multiple,
					unit: edata.uomBeforeConversion,
					emission: edata.solution,
					id: edata.id
				});
				catEmission += edata.solution;
				rows[index] = true;
				// Set live ghg emission data
				edata.children?.forEach(child => {
					if (!liveData[`${child.ghgUnit}`]) {
						liveData[`${child.ghgUnit}`] = {};
					}
					if (liveData[`${child.ghgUnit}`][index]) {
						liveData[`${child.ghgUnit}`][index].push(child.solution);
					} else {
						liveData[`${child.ghgUnit}`][index] = [child.solution];
					}
				});
				if (!liveData[`${edata.ghgUnit}`]) {
					liveData[`${edata.ghgUnit}`] = {};
				}
				if (liveData[`${edata.ghgUnit}`][index]) {
					liveData[`${edata.ghgUnit}`][index].push(edata.solution);
				} else {
					liveData[`${edata.ghgUnit}`][index] = [edata.solution];
				}
				// Setup dummy matoptions also
				matOptions.level1.push([]);
				matOptions.level2.push([]);
				matOptions.level3.push([]);
				matOptions.level4.push([]);
				matOptions.level5.push([]);
				matOptions.uoms.push([]);
			});
			setMaterials(matData);
			setDisabledMatRows(rows);
			setLiveGHGEmission(liveData);
			setMaterialsOptions(matOptions);
			setValidation({});
			fetchCustomFields(matData);
			setAggCatEmission(catEmission);
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const resetMaterialForm = () => {
		setMaterials([{
			level1: '',
			level2: '',
			level3: '',
			level4: '',
			level5: '',
			quantity: '',
			multiple: '',
			unit: '',
			emission: ''
		}]);
		setMaterialsOptions({
			level1: [],
			level2: [],
			level3: [],
			level4: [],
			level5: [],
			uoms: []
		});
		setLiveGHGEmission({});
		setDisabledMatRows({});
		setValidation({});
	};

	const populateAllLevelAndUnitOptionsForRow = (index) => {
		const cat = categories?.find(cat => cat.id === selectedCat);
		let row = {
			index: index,
			level: 1,
			level1: '',
			level2: '',
			level3: '',
			level4: '',
			level5: '',
		};
		const l1 = CarbonEmissionService.getLevels({
			db: emissionTable,
			scope: activeScope,
			category: cat.category,
			level: row.level,
			level1: row.level1,
			level2: row.level2,
			level3: row.level3,
			level4: row.level4,
			level5: row.level5,
		});
		row = {
			index: index,
			level: 2,
			level1: materials[index].level1,
			level2: '',
			level3: '',
			level4: '',
			level5: '',
		};
		const l2 = CarbonEmissionService.getLevels({
			db: emissionTable,
			scope: activeScope,
			category: cat.category,
			level: row.level,
			level1: row.level1,
			level2: row.level2,
			level3: row.level3,
			level4: row.level4,
			level5: row.level5,
		});
		row = {
			index: index,
			level: 3,
			level1: materials[index].level1,
			level2: materials[index].level2,
			level3: '',
			level4: '',
			level5: '',
		};
		const l3 = CarbonEmissionService.getLevels({
			db: emissionTable,
			scope: activeScope,
			category: cat.category,
			level: row.level,
			level1: row.level1,
			level2: row.level2,
			level3: row.level3,
			level4: row.level4,
			level5: row.level5,
		});
		row = {
			index: index,
			level: 4,
			level1: materials[index].level1,
			level2: materials[index].level2,
			level3: materials[index].level3,
			level4: '',
			level5: '',
		};
		const l4 = CarbonEmissionService.getLevels({
			db: emissionTable,
			scope: activeScope,
			category: cat.category,
			level: row.level,
			level1: row.level1,
			level2: row.level2,
			level3: row.level3,
			level4: row.level4,
			level5: row.level5,
		});
		row = {
			index: index,
			level: 5,
			level1: materials[index].level1,
			level2: materials[index].level2,
			level3: materials[index].level3,
			level4: materials[index].level4,
			level5: '',
		};
		const l5 = CarbonEmissionService.getLevels({
			db: emissionTable,
			scope: activeScope,
			category: cat.category,
			level: row.level,
			level1: row.level1,
			level2: row.level2,
			level3: row.level3,
			level4: row.level4,
			level5: row.level5,
		});
		const unit = CarbonEmissionService.getUOM({
			db: emissionTable,
			scope: activeScope,
			level1: materials[index].level1,
			level2: materials[index].level2,
			level3: materials[index].level3,
			level4: materials[index].level4,
			level5: materials[index].level5,
		});
		Promise.all([l1, l2, l3, l4, l5, unit]).then(res => {
			const l1Options = res[0].data.data;
			const l2Options = res[1].data.data;
			const l3Options = res[2].data.data;
			const l4Options = res[3].data.data;
			const l5Options = res[4].data.data;
			const unitOptions = res[5].data.data;
			const matOptions = JSON.parse(JSON.stringify(materialsOptions));
			matOptions.level1[index] = l1Options;
			matOptions.level2[index] = l2Options;
			matOptions.level3[index] = l3Options;
			matOptions.level4[index] = l4Options;
			matOptions.level5[index] = l5Options;
			matOptions.uoms[index] = unitOptions;
			setMaterialsOptions(matOptions);
		}).catch(err => { });
	};

	const validateRows = (index) => {
		const validationClone = JSON.parse(JSON.stringify(validation));
		validationClone[index] = true;
		setValidation(validationClone);
	};

	const getFields = (uom, matIndex) => {
		CarbonEmissionService.getFields({ uom: uom }).then(res => {
			if (res.status !== 200) return;

			setMaterials(mdata => {
				const matData = JSON.parse(JSON.stringify(mdata));
				matData[matIndex]['customFields'] = res.data.data;
				return matData;
			});
		}).catch(err => { });
	};

	const fetchCustomFields = (matData) => {
		matData?.forEach((m, index) => {
			if (!m.multiple) return;

			getFields(m.unit, index);
		});
	};

	const getAggregatedEmission = (scope) => {
		CarbonEmissionService.getAggregatedEmission({
			scope: scope,
			period: filter.reportingPeriod,
			businessUnitId: +filter.entity
		}).then(res => {
			if (res.status !== 200) return;

			setAggEmission(res.data.data);
		}).catch(err => { });
	};

	const getDBData = () => {
		CarbonEmissionService.getDB().then(res => {
			if (res.status !== 200) return;

			setDBData(res.data.data ?? []);
			if (res.data.data?.length > 0) {
				setEmissionTable(res.data.data[0]);
			}
		}).catch(err => { });
	};

	const renderHeader = () => {
		return (
			<div className="carbonEmissionCalculator_header">
				<div>
					<Button variant={activeScope === 'Scope 1' ? 'primary' : 'secondary border-0'}
						onClick={() => onScopeChange('Scope 1')}
						className="sbmBtn mx-2 px-4">
						Scope 1
					</Button>
					<Button variant={activeScope === 'Scope 2' ? 'primary' : 'secondary border-0'}
						onClick={() => onScopeChange('Scope 2')}
						className="sbmBtn mx-2 px-4">
						Scope 2
					</Button>
					<Button variant={activeScope === 'Scope 3' ? 'primary' : 'secondary border-0'}
						onClick={() => onScopeChange('Scope 3')}
						className="sbmBtn mx-2 px-4">
						Scope 3
					</Button>
				</div>
				<div className="Emission_Table_Fillter">
					<ul>
						<li>
							<label htmlFor="emissionTable">{t("EmissionTable.label")}</label>
							<select id="emissionTable"
								className="form-select"
								onChange={(e) => setEmissionTable(e.target.value)}
								required
								value={emissionTable}>
								{
									dbData.map(d => {
										return <option key={d} value={d}>{d}</option>;
									})
								}
							</select>
						</li>
					</ul>
				</div>
			</div>
		)
	};

	const renderCategories = () => {
		return (
			<div>
				<h6>{t('SelectEmissionSource.label')}</h6>
				<div className="list_SelectEmissionSource">
					{
						categories?.map(cat => {
							return (
								<div className={`Single_EmissionSource_card  ${selectedCat === cat.id ? 'EmissionSource-selected' : ''}`}
									onClick={() => onCatSelect(cat)}>
									<div className="cat-icon">
										<img src={require(`../../../images/emission/${cat.icon}`)} />
									</div>
									{cat.category}
								</div>
							);
						})
					}
				</div>
				{selectedCat > 0 && <p>{categories?.find(cat => cat.id === selectedCat)?.description}</p>}
			</div>
		);
	};

	const renderMaterialsQuantity = () => {
		if (selectedCat === 0) return <></>;

		return (
			<div className="mt-4">
				<h6>{t("QuantityOfMaterials.label")}</h6>
				<div className="mt-4">
					<div className="d-flex">
						<div className="w-10 mx-2">{t("Level1.label")}</div>
						<div className="w-10 mx-2">{t("Level2.label")}</div>
						<div className="w-10 mx-2">{t("Level3.label")}</div>
						<div className="w-15 mx-2">{t("Level45.label")}</div>
						<div className="w-15 mx-2">{t("Quantity.label")}</div>
						<div className="w-15 mx-2">{t("Unit.label")}</div>
						<div className="w-15 mx-2 Carbon_Emission_head">{t("CarbonEmissionWithUnit.label")}</div>
						<div className="w-5"></div>
					</div>
					{
						materials?.map((material, index) => {
							return renderMaterialFormRow(material, index);
						})
					}
				</div>
			</div>
		)
	};

	const renderLevel1Field = (material, index) => {
		return (
			<div className="w-10 mx-2">
				{
					disabledMatRows[index] === true
						?
						<input className="form-control"
							disabled={true}
							value={material.level1} />
						:
						<select
							className="form-select"
							onChange={(e) => onLevel1Change(e.target.value, index)}
							required={true}
							value={material.level1}>
							<option key='' value='' hidden>{t('Select.label')}</option>
							{
								materialsOptions.level1?.length > 0 &&
								materialsOptions.level1[index]?.map((l, lindex) => {
									return (
										<option value={l} key={`level1-${index}-${lindex}`}>{l}</option>
									);
								})
							}
						</select>
				}
			</div>
		)
	};

	const renderLevel2Field = (material, index) => {
		return (
			<div className="w-10 mx-2">
				{
					disabledMatRows[index] === true
						?
						<input className="form-control"
							disabled={true}
							value={material.level2} />
						:
						<select
							className="form-select"
							onChange={(e) => onLevel2Change(e.target.value, index)}
							required={true}
							value={material.level2}>
							<option key='' value='' hidden>{t('Select.label')}</option>
							{
								materialsOptions.level2?.length > 0 &&
								materialsOptions.level2[index]?.map((l, lindex) => {
									return <option value={l} key={`level2-${index}-${lindex}`}>{l}</option>;
								})
							}
						</select>
				}
			</div>
		);
	};

	const renderLevel3Field = (material, index) => {
		return (
			<div className="w-10 mx-2">
				{
					disabledMatRows[index] === true
						?
						<input className="form-control"
							disabled={true}
							value={material.level3} />
						:
						<select
							className="form-select"
							onChange={(e) => onLevel3Change(e.target.value, index)}
							required={true}
							value={material.level3}>
							<option key='' value='' hidden>{t('Select.label')}</option>
							{
								materialsOptions.level3?.length > 0 &&
								materialsOptions.level3[index]?.map((l, lindex) => {
									return <option value={l} key={`level3-${index}-${lindex}`}>{l}</option>;
								})
							}
						</select>
				}
			</div>
		);
	};

	const renderLevel4To5Field = (material, index) => {
		return (
			<div className="w-15 mx-2">
				<div className="d-flex w-100">
					<div className="w-50 me-1">
						{
							disabledMatRows[index] === true
								?
								<input className="form-control"
									disabled={true}
									value={material.level4} />
								:
								<select
									className="form-select"
									onChange={(e) => onLevel4Change(e.target.value, index)}
									required={true}
									value={material.level4}>
									<option key='' value='' hidden>{t('Select.label')}</option>
									{
										materialsOptions.level4?.length > 0 &&
										materialsOptions.level4[index]?.map((l, lindex) => {
											return <option value={l} key={`level4-${index}-${lindex}`}>{l}</option>;
										})
									}
								</select>
						}
					</div>
					<div className="w-50">
						{
							disabledMatRows[index] === true
								?
								<input className="form-control"
									disabled={true}
									value={material.level5} />
								:
								<select
									className="form-select"
									onChange={(e) => onLevel5Change(e.target.value, index)}
									required={true}
									value={material.level5}>
									<option key='' value='' hidden>{t('Select.label')}</option>
									{
										materialsOptions.level5?.length > 0 &&
										materialsOptions.level5[index]?.map((l, lindex) => {
											return <option value={l} key={`level4-${index}-${lindex}`}>{l}</option>;
										})
									}
								</select>
						}
					</div>
				</div>
			</div>
		);
	};

	const renderQuantityField = (material, index) => {
		if (material.customFields?.length > 1) {
			return (
				<div className="mx-2 w-15">
					<div className="d-flex w-100">
						<div className="w-50 me-1">
							<OverlayTrigger
								placement="top"
								overlay={<Tooltip id="button-tooltip">{material.customFields[0]}</Tooltip>}>
								<input className="form-control"
									placeholder={material.customFields[0]}
									required={true}
									type="number"
									value={material.quantity}
									disabled={disabledMatRows[index] === true}
									onChange={(e) => onQuantityChange(e.target.value, index)}
									onBlur={(e) => onQuantityBlur(e.target.value, index)} />
							</OverlayTrigger>
						</div>
						<div className="w-50">
							<OverlayTrigger
								placement="top"
								overlay={<Tooltip id="button-tooltip">{material.customFields[1]}</Tooltip>}>
								<input className="form-control"
									placeholder={material.customFields[1]}
									required={true}
									type="number"
									value={material.multiple}
									disabled={disabledMatRows[index] === true}
									onChange={(e) => onMultipleChange(e.target.value, index)}
									onBlur={(e) => onMultipleBlur(e.target.value, index)} />
							</OverlayTrigger>
						</div>
					</div>
				</div>
			);

		} else {
			return (
				<div className="w-15 mx-2">
					<input className="form-control"
						required={true}
						type="number"
						value={material.quantity}
						disabled={disabledMatRows[index] === true}
						onChange={(e) => onQuantityChange(e.target.value, index)}
						onBlur={(e) => onQuantityBlur(e.target.value, index)} />
				</div>
			);
		}
	};

	const renderUnitField = (material, index) => {
		return (
			<div className="w-15 mx-2">
				{
					disabledMatRows[index] === true
						?
						<input className="form-control"
							disabled={true}
							value={material.unit} />
						:
						<select id="esgFilter"
							className="form-select"
							onChange={(e) => onUnitChange(e.target.value, index)}
							required={true}
							value={materials[index].unit}>
							<option key='' value='' hidden>{t('Select.label')}</option>
							{
								materialsOptions.uoms?.length > 0 &&
								materialsOptions.uoms[index]?.map((uom, uindex) => {
									return <option value={uom} key={`uom-${index}-${uindex}`}>{uom}</option>;
								})
							}
						</select>
				}
			</div>
		);
	};

	const renderEmissionField = (material, index) => {
		return (
			<div className="w-15 mx-2">
				<input className="form-control"
					required={true}
					disabled={true}
					value={material.emission}
					onChange={null} />
			</div>
		);
	};

	const renderMaterialFormRow = (material, index) => {
		return (
			<Form validated={validation[index] === true}>
				<div className="d-flex material-row my-2" id={`matRow-${index}`} key={`matRow-${index}`}>
					{renderLevel1Field(material, index)}
					{renderLevel2Field(material, index)}
					{renderLevel3Field(material, index)}
					{renderLevel4To5Field(material, index)}
					{renderQuantityField(material, index)}
					{renderUnitField(material, index)}
					{renderEmissionField(material, index)}
					<div className="w-5 action-cell">
						{
							materials?.length > 1 &&
							<span className="mx-1 cursor"
								onClick={() => onDeleteRow(index, material.id)}>
								<AiOutlineCloseCircle />
							</span>
						}
						{
							index === (materials?.length - 1) &&
							<span className="mx-1 cursor"
								onClick={() => onAddRow(index)}>
								<MdAddCircleOutline />
							</span>
						}
						{
							disabledMatRows[index] === true &&
							<span className="mx-1 cursor"
								onClick={() => onEditRow(index)}>
								<BiEdit />
							</span>
						}
						{
							disabledMatRows[index] === false &&
							<span className="mx-1 cursor"
								onClick={() => onSaveRow(index)}>
								<BiCheckCircle />
							</span>
						}
					</div>
				</div>
			</Form>
		);
	};

	const renderFooter = () => {
		const keys = Object.keys(liveGHGEmission);
		if (keys?.length === 0) return <></>;

		return (
			<div className="card mt-4 p-2">
				<div className="source_emission-AllCount justify-content-end">
					<ul>
						{
							keys?.map((k, index) => {
								if (k === 'kg CO2e') return <></>;

								return (
									<>
										<li key={`footerItem-${index}`} id={`footerItem-${index}`}>
											<div>
												<span className="text-primary fw-bold">{getGHGAggregate(k).toFixed(2)}</span>
											</div>
											<div>
												{k}
											</div>
										</li>
									</>
								);
							})
						}
						{
							<li key={`footerItemKG`} id={`footerItemKG`}>
								<div className="Emission_totalCount_01">
									<h4 className="text-primary fw-bold m-0">
										{getGHGAggregate('kg CO2e').toFixed(2)}

									</h4>
									<span>kg CO2e	</span>
								</div>
								<div className="fw-bold">
									{t('TotalCarbonEmission.label')}
								</div>
							</li>
						}
					</ul>

					<Button variant="primary"
						onClick={() => onSubmit()}
						className="sbmBtn mx-2 px-5">
						{t("Save.label")}
					</Button>
				</div>
			</div>
		)
	};

	const renderAggregatedEmissionCard = () => {
		let relativeTotal = aggEmission;
		if (selectedCat === 0) {
			relativeTotal = aggEmission;
		} else if (liveGHGEmission['kg CO2e']) {
			relativeTotal = (aggEmission - aggCatEmission) + getGHGAggregate('kg CO2e');
		}
		return (
			<div className="card mt-4 p-2">
				<div className="source_emission-AllCount">
					<ul>
						<li key={`footerItemKG`} id={`footerItemKG`}>
							<div className="Emission_totalCount_01">
								<h4 className="text-primary fw-bold m-0">
									{(relativeTotal ?? 0).toFixed(2)}
								</h4>
								<span>kg CO2e	</span>
							</div>
							<div className="fw-bold">
								{`${t('Total.label')} ${activeScope} ${t('Emission.label')}`}
							</div>
						</li>
						<li key={`footerItemKG`} id={`footerItemKG`}>
							<div className="Emission_totalCount_01">
								<h4 className="text-primary fw-bold m-0">
									{((relativeTotal ?? 0) / 1000).toFixed(2)}
								</h4>
								<span>t CO2e	</span>
							</div>
							<div className="fw-bold">
								{`${t('Total.label')} ${activeScope} ${t('Emission.label')}`}
							</div>
						</li>
					</ul>
				</div>
			</div>
		)
	};
	return (
		<div id='carbonEmissionCalculator' className="mt-3 card carbonEmissionCalculator_Card">
			{renderHeader()}
			<div className="p-4">
				<div className="text-muted">
					{activeScope === 'Scope 1' && <p>{t('Scope1Definition.label')}</p>}
					{activeScope === 'Scope 2' && <p>{t('Scope2Definition.label')}</p>}
					{activeScope === 'Scope 3' && <p>{t('Scope3Definition.label')}</p>}
				</div>
				{renderCategories()}
				{renderMaterialsQuantity()}
				{renderFooter()}
				{renderAggregatedEmissionCard()}
			</div>
			{
				(showPrompt || showWarning) && <ConfirmationModal
					modalShow={showPrompt || showWarning}
					title={t("confirmation.label")}
					messageText={t("UnsavedChanges.label")}
					callbackModal={(yes, id) => {
						if (yes) {
							setDirty(false);



							if (pausedScopeToNavigate) {
								setMaterialsOptions({
									level1: [],
									level2: [],
									level3: [],
								});
								setSelectedCat(0);
								setActiveScope(pausedScopeToNavigate);
							} else if (pausedCatToNavigate > 0) {
								setSelectedCat(pausedCatToNavigate);
							} else {
								confirmNavigation();
							}
							setShowWarning(false);
						} else {
							cancelNavigation();
							setShowWarning(false);
						}
					}}
					confirmBtn={t("Yes.label")}
					CancelBtn={t("No.label")}
				/>
			}
			{
				dataToDelete.id > 0 &&
				<ConfirmationModal
					modalShow={dataToDelete.id > 0}
					messageText={t("EmissionDataDeletionConfirmation.label")}
					callbackModal={(yes, id) => {
						const index = dataToDelete.index;
						if (yes) {
							CarbonEmissionService.deleteEmissionData(dataToDelete.id, glbRegulation?.id).then(res => {
								if (res.status !== 200 && res.status !== 204) return;

								deleteAtIndex(index);
							}).catch(err => { });
						}
						setDataToDelete({});
					}}
					confirmBtn={t("Yes.label")}
					CancelBtn={t("No.label")}
				/>
			}
		</div>
	);
};

export default withTranslation()(CarbonEmissionCalculator);
